import React, { FC, useState, useEffect } from 'react'

// Types
import { MenuProps, IDrawerTheme } from './Menu.types'

// Styles
import classnames from 'classnames'
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Theme,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'

// Images
import AddIcon from '@material-ui/icons/Add'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import liveChatIcon from '../assets/images/live-chat-icon.svg'
import arrowLeftIcon from '../assets/images/arrow-left-icon.svg'
import arrowRightIcon from '../assets/images/arrow-right-icon.svg'

export const Menu: FC<MenuProps> = ({
  drawerTheme,
  drawerOpen,
  setDrawerOpen,
  headerLogoOpen,
  headerLogoClose,
  userGreeting,
  user,
  navigationItems,
  liveChat,
  liveChatAction,
  liveChatChannels,
  liveChatDirectMessages
}) => {
  const classes = useStyles(drawerTheme)

  const [subMenuOpenIndex, setSubMenuOpenIndex] = useState<number | null>(null)

  const handleToggleSubMenu = (index: number) => {
    if (drawerOpen === false) {
      setDrawerOpen(true)
    }

    setSubMenuOpenIndex(subMenuOpenIndex === index ? null : index)
  }

  useEffect(() => {
    if (drawerOpen === false) {
      setSubMenuOpenIndex(null)
    }
  }, [drawerOpen])

  return (
    <>
      <Drawer
        anchor='right'
        open={drawerOpen}
        data-testid='Menu'
        variant='permanent'
        className={classnames(classes.drawer, {
          [classes.drawerOpen]: drawerOpen,
          [classes.drawerClose]: !drawerOpen
        })}
        classes={{
          paper: classnames(classes.paper, {
            [classes.drawerOpen]: drawerOpen,
            [classes.drawerClose]: !drawerOpen
          })
        }}
      >
        {/* Header logo */}
        <Box className={classes.header} component='header'>
          <Fade in={drawerOpen === true}>
            <img className={classes.logo} src={headerLogoOpen.source} alt='Logo open' width={headerLogoOpen.width} />
          </Fade>
          <Fade in={drawerOpen === false}>
            <img className={classes.logo} src={headerLogoClose.source} alt='Logo close' width={headerLogoClose.width} />
          </Fade>
        </Box>

        {/* User info */}
        <List>
          <ListItem>
            <ListItemAvatar>
              <Avatar className={classes.avatar} src={user?.avatar} alt={user?.firstName}>
                {user?.firstName?.substring(0, 1)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography component='span' variant='h6'>
                  {userGreeting}, {user?.firstName}
                </Typography>
              }
              secondary={user?.company}
            />
          </ListItem>
        </List>

        {/* Menu list items */}
        <List className={classes.list} component='nav' disablePadding>
          {navigationItems.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem
                button
                onClick={() => {
                  item.action && item.action()
                  item.submenuItems?.length && handleToggleSubMenu(index)
                }}
              >
                <Tooltip title={item.title} placement='left'>
                  <ListItemIcon>
                    <img src={item.icon} alt={item.title} />
                  </ListItemIcon>
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography component='h5' variant='h5'>
                      {item.title}
                    </Typography>
                  }
                />
                {item.submenuItems?.length && (
                  <>{subMenuOpenIndex === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}</>
                )}
              </ListItem>
              <Collapse in={subMenuOpenIndex === index} timeout='auto' unmountOnExit>
                <List component='nav' dense disablePadding>
                  {item.submenuItems?.map((subItem, index) => (
                    <ListItem key={index} button onClick={subItem.action}>
                      <ListItemText
                        primary={
                          <Typography component='span' variant='subtitle1' gutterBottom={false}>
                            {subItem.title}
                          </Typography>
                        }
                        secondary={subItem.subtitle}
                        inset
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>

        {/* Live chat */}
        {liveChat && (
          <>
            <Divider className={classes.divider} />
            <List disablePadding>
              <ListItem>
                <Tooltip title='Live Chat' placement='left'>
                  <ListItemIcon>
                    <Badge className={classes.badge} color='primary' variant='dot'>
                      <img src={liveChatIcon} alt='Live Chat' />
                    </Badge>
                  </ListItemIcon>
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography component='h5' variant='h5'>
                      Live Chat
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={liveChatAction}>
                    <AddIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            <div className={classes.list}>
              {/* Channels */}
              <List>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography component='h6' variant='h6' gutterBottom={false}>
                        Channels
                      </Typography>
                    }
                    inset
                  />
                </ListItem>
                {liveChatChannels?.map((channel, index) => (
                  <ListItem key={index} dense>
                    <Badge
                      className={classnames(classes.badge, classes.badgeCenter)}
                      badgeContent={channel.previewCount}
                      color='primary'
                    >
                      <ListItemText
                        primary={
                          <>
                            <i
                              className={classnames(classes.status, {
                                [classes.statusOffline]: channel.isDirectMessage,
                                [classes.statusOnline]: !channel.isDirectMessage
                              })}
                            />
                            {channel.title}
                          </>
                        }
                        inset
                      />
                    </Badge>
                    <ListItemSecondaryAction>
                      <IconButton onClick={channel.action}>
                        <MoreHorizIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              {/* Direct messaging */}
              <List disablePadding>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography component='h6' variant='h6' gutterBottom={false}>
                        Direct Messaging
                      </Typography>
                    }
                    inset
                  />
                </ListItem>
                {liveChatDirectMessages?.map((message, index) => (
                  <ListItem key={index} dense>
                    <Badge
                      className={classnames(classes.badge, classes.badgeCenter)}
                      badgeContent={message.previewCount}
                      color='primary'
                    >
                      <ListItemText
                        primary={
                          <>
                            <i
                              className={classnames(classes.status, {
                                [classes.statusOffline]: message.offline,
                                [classes.statusOnline]: !message.offline
                              })}
                            />
                            {message.title}
                          </>
                        }
                        inset
                      />
                    </Badge>
                    <ListItemSecondaryAction>
                      <IconButton onClick={message.action}>
                        <MoreHorizIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </div>
          </>
        )}

        {/* Footer */}
        <Box className={classnames(classes.footer, { [classes.footerClose]: !drawerOpen })} component='footer'>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            {drawerOpen ? (
              <img src={arrowRightIcon} alt='Arrow right' width='16px' />
            ) : (
              <img src={arrowLeftIcon} alt='Arrow left' width='16px' />
            )}
          </IconButton>
        </Box>
      </Drawer>
    </>
  )
}

const drawerWidth = 334
const drawerMinWidth = 64

const useStyles = makeStyles((theme: Theme) => ({
  drawer: (props: IDrawerTheme) => ({
    flexShrink: 0,
    width: drawerWidth,
    whiteSpace: 'nowrap',
    position: 'absolute',
    color: props.textColor,
    backgroundColor: props.backgroundColor,

    '& .MuiIconButton-root': {
      color: props.textColor
    },

    '& .MuiListItemText-secondary': {
      color: props.textColor
    }
  }),
  drawerOpen: {
    width: drawerWidth,

    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerClose: {
    overflowX: 'hidden',
    width: drawerMinWidth,

    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),

    '& .MuiListItem-gutters': {
      paddingLeft: '20px'
    },

    '& .MuiListItemSecondaryAction-root': {
      display: 'none'
    }
  },
  paper: (props: IDrawerTheme) => ({
    borderLeft: '0',
    maxHeight: '100%',
    overflow: 'hidden',
    paddingBottom: '50px',
    color: props.textColor,
    backgroundColor: props.backgroundColor
  }),
  header: (props: IDrawerTheme) => ({
    position: 'relative',
    display: 'flex',
    minHeight: '73px',
    maxHeight: '73px',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: props.headerBackgroundColor
  }),
  logo: {
    position: 'absolute',
    maxHeight: '90%',
    objectFit: 'contain'
  },
  avatar: {
    width: '25px',
    height: '25px',
    fontSize: '12px',
    fontWeight: 'bold',
    border: '1px solid #ffffff',
    backgroundColor: 'transparent'
  },
  list: {
    overflow: 'auto'
  },
  divider: {
    margin: '44px 0 20px',
    backgroundColor: '#747480'
  },
  badge: (props: IDrawerTheme) => ({
    '& .MuiBadge-colorPrimary': {
      color: props.badgeTextColor,
      backgroundColor: props.badgeBackgroundColor
    }
  }),
  badgeCenter: {
    '& .MuiBadge-anchorOriginTopRightRectangle': {
      top: '50%',
      right: '-18px'
    }
  },
  status: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    marginRight: '10px'
  },
  statusOnline: {
    backgroundColor: '#2DB757'
  },
  statusOffline: {
    backgroundColor: '#D8DADA'
  },
  footer: (props: IDrawerTheme) => ({
    bottom: 0,
    zIndex: 1,
    width: '100%',
    display: 'flex',
    position: 'absolute',
    backgroundColor: props.backgroundColor
  }),
  footerClose: {
    justifyContent: 'center'
  }
}))
